import React, { useState, useEffect } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMapEvents,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Import icon marker (Leaflet's default icon might not load correctly in React)
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { positionMarker } from "../objects/position_marker";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { LuMapPin } from "react-icons/lu";
export interface CoordinatePickerProps {
  hideSearch?: boolean;
  dragging?: boolean;
  onPicker: (p: positionMarker) => void;
  lat?: number | null;
  lng?: number | null;
}

const DefaultIcon = L.icon({
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
  iconAnchor: [12, 41], // point of the icon which will correspond to marker's location
});

L.Marker.prototype.options.icon = DefaultIcon;

// Component to update the map center
const MapCenterUpdater: React.FC<{ center: [number, number] }> = ({
  center,
}) => {
  const map = useMap();
  map.setView(center);
  map.setZoom(17);
  return null;
};

const CoordinatePicker: React.FC<CoordinatePickerProps> = ({
  hideSearch = false,
  dragging = false,
  onPicker,
  lat,
  lng,
}) => {
  const [position, setPosition] = useState<{ lat: number; lng: number } | null>(
    null
  );
  const [mapCenter, setMapCenter] = useState<[number, number] | null>(null);
  const [mounted, setMounted] = useState(false);
  const [query, setQuery] = useState<string>("");
  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;
    console.log("MOUNTED");
    if (lat && lng) {
      setTimeout(() => {
        setPosition({ lat: lat, lng: lng });
      }, 300);
      return;
    } else {
      resetLocation();
    }
    // Get user's current location
  }, [lat, lng, mounted]);

  const resetLocation = () => {
    console.log("RESET");
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setPosition({ lat: latitude, lng: longitude });
          setMapCenter([latitude, longitude]);
        },
        (error) => {
          setMapCenter([-6.9347788, 107.6022257]);
        }
      );
    }
  };

  useEffect(() => {
    if (position) {
      setTimeout(() => {
        setMapCenter([position.lat, position.lng]);
      }, 300);
    }
  }, [position]);

  const MapClickHandler = () => {
    useMapEvents({
      click(e) {
        onPicker({
          lat: e.latlng.lat,
          lng: e.latlng.lng,
        });
        setPosition(e.latlng); // Update state with clicked position
      },
    });
    return position === null ? null : <Marker position={position} />; // Render marker on clicked position
  };

  const searchLocation = async () => {
    try {
      const params: Record<string, any> = {
        q: query,
        format: "json",
        limit: 1,
      };
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?${new URLSearchParams(
          params
        )}`
      );
      const respJson = await response.json();
      if (respJson.length > 0) {
        const { lat, lon } = respJson[0];
        const newPosition = { lat: parseFloat(lat), lng: parseFloat(lon) };
        setPosition(newPosition);

        onPicker({
          lat: parseFloat(lat),
          lng: parseFloat(lon),
        });
      } else {
        alert("Location not found");
      }
    } catch (error) {
      console.error("Error searching location: ", error);
      alert("Error searching location");
    }
  };

  return (
    <div className="mb-4 p-4 bg-gray-50 rounded-lg">
      {!hideSearch && (
        <div className="relative">
          <div className="absolute top-2.5 start-0 flex items-center ps-3 pointer-events-none">
            <MagnifyingGlassIcon
              className=" h-5 w-5 text-violet-200 hover:text-violet-100"
              aria-hidden="true"
            />
          </div>
          <div className="absolute top-2.5 z-50  right-2 cursor-pointer flex items-center ps-3 pointer-events-none">
            <LuMapPin
              className=" h-5 w-5 text-violet-200 hover:text-violet-100"
              aria-hidden="true"
              onClick={resetLocation}
            />
          </div>
          <input
            autoComplete="off"
            value={query}
            type="search"
            id="default-search"
            className="block  outline-1  outline-blue-300  w-full py-2 px-4 text-sm ps-10 pe-10  text-gray-900 border rounded-full bg-white   mb-4"
            placeholder={"Cari Lokasi tekan enter ..."}
            onChange={(val) => setQuery(val.target.value)}
            onKeyUp={(val) => {
              if (val.key == "Enter") {
                searchLocation();
              }
            }}
          />
        </div>
      )}

      {mapCenter ? (
        <MapContainer
          dragging={dragging}
          center={mapCenter} // Set center to user's location or default
          zoom={13}
          style={{ height: "300px", width: "100%" }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          {dragging ? <MapClickHandler /> : <Marker position={mapCenter} />}
          
          <MapCenterUpdater center={mapCenter} />
        </MapContainer>
      ) : (
        <div>Reset Location</div>
      )}
    </div>
  );
};

export default CoordinatePicker;
